import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Slate, Editable, withReact } from 'slate-react';
import { createEditor, Descendant, Editor, Text, Range, Transforms, Point } from 'slate';
import { Button, ButtonGroup, Icon } from "semantic-ui-react";
import styles from "./index.module.css";

type TextFormat = 'bold' | 'italic' | 'underline';

interface SlateEditableTextProps {
    value: Descendant[];
    onChange: (value: Descendant[]) => void;
    maxLength?: number;
    disabled?: boolean;
}

const SlateEditableText: React.FC<SlateEditableTextProps> = (
    {
        value,
        onChange,
        maxLength= 300,
        disabled= false
    }) => {
    const editor = useMemo(() => withReact(createEditor()), []);
    const [activeFormats, setActiveFormats] = useState<Set<TextFormat>>(new Set());

    const countTextLength = useCallback((nodes: Descendant[]): number => {
        return nodes.reduce((count: number, node) => {
            if (Text.isText(node)) {
                return count + node.text.length;
            }
            return count + countTextLength(node.children);
        }, 0);
    }, []);

    const handleChange = useCallback((newValue: Descendant[]) => {
        const isAstChange = editor.operations.some(op => op.type !== 'set_selection');
        if (!isAstChange) {
            return;
        }

        const length = countTextLength(newValue);
        if (length <= maxLength) {
            onChange(newValue);
        } else {
            // Если текст превысил лимит, восстанавливаем предыдущее значение
            onChange(value);
        }
    }, [maxLength, onChange, countTextLength, value, editor]);

    const updateActiveFormats = useCallback(() => {
        const newActiveFormats = new Set<TextFormat>();
        const marks = Editor.marks(editor) || {};

        if (marks.bold) newActiveFormats.add('bold');
        if (marks.italic) newActiveFormats.add('italic');
        if (marks.underline) newActiveFormats.add('underline');

        setActiveFormats(newActiveFormats);
    }, [editor]);

    useEffect(() => {
        const { onChange: editorOnChange } = editor;

        editor.onChange = () => {
            updateActiveFormats();
            editorOnChange();
        };

        return () => {
            editor.onChange = editorOnChange;
        };
    }, [editor, updateActiveFormats]);

    const toggleMark = useCallback((editor: Editor, format: TextFormat) => {
        const { selection } = editor;

        if (!selection) return;

        const isCollapsed = Range.isCollapsed(selection);
        const marks = Editor.marks(editor) || {};
        const isActive = !!marks[format];

        if (isCollapsed) {
            if (isActive) {
                Editor.removeMark(editor, format);
            } else {
                Editor.addMark(editor, format, true);
            }
        } else {
            Transforms.setNodes(
                editor,
                { [format]: !isActive },
                { match: n => Text.isText(n), split: true, at: selection }
            );
        }
    }, []);

    const isMarkActive = useCallback((format: TextFormat) => {
        return activeFormats.has(format);
    }, [activeFormats]);

    const handlePaste = useCallback((event: React.ClipboardEvent) => {
        event.preventDefault();
        const pasteText = event.clipboardData.getData('text/plain');
        const currentLength = countTextLength(value);
        const remainingLength = maxLength - currentLength;

        if (remainingLength <= 0) return;

        const textToInsert = pasteText.slice(0, remainingLength);
        editor.insertText(textToInsert);
    }, [editor, value, maxLength, countTextLength]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        const key = event.key.toLowerCase();
        const isCtrl = event.ctrlKey || event.metaKey;

        if (isCtrl && (key === 'arrowleft' || key === 'arrowright')) {
            event.preventDefault();

            const { selection } = editor;
            if (!selection) return;

            const [start] = Range.edges(selection);

            const direction = key === 'arrowleft' ? -1 : 1;

            const newPoint = direction === -1
                ? Editor.before(editor, start, { unit: 'word' })
                : Editor.after(editor, start, { unit: 'word' });

            if (newPoint) {
                Transforms.select(editor, { anchor: newPoint, focus: newPoint });
            }
            return;
        }

        const keyMappings: Record<string, string> = {
            'ф': 'a', 'с': 'c', 'ч': 'x', 'м': 'v', 'я': 'z', 'н': 'y'
        };

        const normalizedKey = keyMappings[key] || key;

        // Разрешаем стандартные комбинации клавиш
        if (isCtrl && !event.shiftKey &&
            ['a', 'c', 'x', 'v', 'z', 'y',
                'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(normalizedKey)) {
            return;
        }

        // Запрещаем ввод при достижении лимита
        const currentLength = countTextLength(value);
        if (currentLength >= maxLength && ![
            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End', 'Tab', 'Enter', 'Escape'
        ].includes(event.key)) {
            event.preventDefault();
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const marks = Editor.marks(editor) || {};
            const currentLength = countTextLength(value);

            if (currentLength >= maxLength) {
                return;
            }

            editor.insertText('\n');

            if (marks.bold) Editor.addMark(editor, 'bold', true);
            if (marks.italic) Editor.addMark(editor, 'italic', true);
            if (marks.underline) Editor.addMark(editor, 'underline', true);
            return;
        }

        if (isCtrl && !event.shiftKey) {
            event.preventDefault();
            switch (normalizedKey) {
                case 'b': case 'и': toggleMark(editor, 'bold'); break;
                case 'i': case 'ш': toggleMark(editor, 'italic'); break;
                case 'u': case 'г': toggleMark(editor, 'underline'); break;
            }
        }
    }, [editor, value, maxLength, countTextLength, toggleMark]);

    const FormatButton = React.useMemo(() =>
        ({ format, icon }: { format: TextFormat; icon: string }) => (
            <Button
                active={isMarkActive(format)}
                onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                    toggleMark(editor, format);
                }}
            >
                <Icon name={format} />
            </Button>
        ), [isMarkActive, toggleMark, editor]);

    const renderLeaf = useCallback(({ attributes, children, leaf }: any) => {
        if (leaf.bold) children = <strong>{children}</strong>;
        if (leaf.italic) children = <em>{children}</em>;
        if (leaf.underline) children = <u>{children}</u>;
        return <span {...attributes}>{children}</span>;
    }, []);

    return (
        <Slate editor={editor} value={value} onChange={handleChange}>
            <div className={styles.editableContainer}>
                {!disabled && (
                    <ButtonGroup size="tiny" className={styles.formattingPanel}>
                        <FormatButton format="bold" icon="bold" />
                        <FormatButton format="italic" icon="italic" />
                        <FormatButton format="underline" icon="underline" />
                    </ButtonGroup>
                )}
                <div className={styles.counter}>
                    Введено: {countTextLength(value)}/{maxLength}
                </div>
            </div>
            <Editable
                className={styles.editableText}
                readOnly={disabled}
                placeholder="Введите текст..."
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                renderLeaf={renderLeaf}
            />
        </Slate>
    );
};

export default SlateEditableText;